import { TOKEN_EXPIRED_MILI_SECONDS } from "../common/settings";

/**
 * ローカルストレージのキー
 */
const LOCAL_STORAGE_KEY = {
  /** アクセストークンのキー */
  ACCESS_TOKEN: "sc20_app_access_token",
  /** リフレッシュトークンのキー */
  REFRESH_TOKEN: "sc20_app_refresh_token",
  /** CODE_VERIFIER のキー */
  CODE_VERIFIER: "sc20_app_code_verifier",
  /** ログイン時間のキー */
  LOGIN_TIME: "sc20_app_login_time",
  /** ログインユーザーの極のキー */
  LOGIN_POLE: "sc20_app_login_pole",
} as const;

/**
 * 認証用のアイテムに関するインタフェースです。
 */
export interface AuthItems {
  /** アクセストークン */
  accessToken: string;
  /** リフレッシュトークン */
  refleshToken: string;
  /** CODE_VERIFIER */
  codeVerifier: string;
  /** ログイン時間 */
  loginTime: string;
  /** ログインしているユーザーの極 */
  loginPole: string;

  /**
   * アクセストークンを削除します。
   */
  removeAccessToken(): void;
  /**
   * リフレッシュトークンを削除します。
   */
  removeRefleshToken(): void;
  /**
   * CODE_VERIFIERを削除します。
   */
  removeCodeVerifier(): void;
  /**
   * ログイン時間を削除します。
   */
  removeLoginTime(): void;
  /**
   * 全てのアイテムを削除します。
   */
  removeAll(): void;
  /**
   * アクセストークンが期限切れかどうかを判断します。
   *
   * @returns アクセストークンが期限切れの場合はtrue
   */
  isExpiredAccessToken(): boolean;
}

class AuthItemsImpl implements AuthItems {
  get accessToken(): string {
    return this.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
  }

  set accessToken(value: string) {
    this.setItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN, value);
  }

  get refleshToken(): string {
    return this.getItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN);
  }

  set refleshToken(value: string) {
    this.setItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN, value);
  }

  get codeVerifier(): string {
    return this.getItem(LOCAL_STORAGE_KEY.CODE_VERIFIER);
  }

  set codeVerifier(value: string) {
    this.setItem(LOCAL_STORAGE_KEY.CODE_VERIFIER, value);
  }

  get loginTime(): string {
    return this.getItem(LOCAL_STORAGE_KEY.LOGIN_TIME);
  }

  set loginTime(value: string) {
    this.setItem(LOCAL_STORAGE_KEY.LOGIN_TIME, value);
  }

  get loginPole(): string {
    return this.getItem(LOCAL_STORAGE_KEY.LOGIN_POLE);
  }

  set loginPole(value: string) {
    this.setItem(LOCAL_STORAGE_KEY.LOGIN_POLE, value);
  }

  removeAccessToken(): void {
    return window.localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
  }

  removeRefleshToken(): void {
    return window.localStorage.removeItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN);
  }

  removeCodeVerifier(): void {
    return window.localStorage.removeItem(LOCAL_STORAGE_KEY.CODE_VERIFIER);
  }

  removeLoginTime(): void {
    return window.localStorage.removeItem(LOCAL_STORAGE_KEY.LOGIN_TIME);
  }

  removeLoginPole(): void {
    return window.localStorage.removeItem(LOCAL_STORAGE_KEY.LOGIN_POLE);
  }

  removeAll(): void {
    this.removeAccessToken();
    this.removeRefleshToken();
    this.removeCodeVerifier();
    this.removeLoginTime();
    this.removeLoginPole();
  }

  private getItem(key: string): string {
    const value = window.localStorage.getItem(key);
    return value ?? "";
  }

  private setItem(key: string, value: string): void {
    window.localStorage.setItem(key, value);
  }

  /**
   * アクセストークンが期限切れかどうかを判断します。
   *
   * @returns アクセストークンが期限切れの場合はtrue、そうでない場合はfalse
   */
  isExpiredAccessToken(): boolean {
    if (!this.accessToken || !this.loginTime) {
      return true;
    }

    const now = Date.now();
    const elapsedime = now - Number.parseInt(this.loginTime);
    return elapsedime > TOKEN_EXPIRED_MILI_SECONDS;
  }
}

const authItems: AuthItems = new AuthItemsImpl();

/**
 * 認証アイテム  {@link AuthItems} を取得します。
 * @returns 認証アイテム
 */
export const getAuthItems = (): AuthItems => {
  return authItems;
};
